import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import PageContainer from "@/components/layouts/PageContainer";
import { Heading } from "@/components/heading";
import { Text } from "@/components/text";
import { Button } from "@/components/button";
import { ArrowLeftIcon } from "@heroicons/react/20/solid";
import useUpdateStudent from "@/apis/users/updateUser";
import type { IUpdateStudentRequest } from "@/apis/users/updateUser";
import useQueryGetUser from "@/apis/users/getUser";
import StudentForm from "./components/StudentForm";
import type { ICreateStudentRequest } from "@/apis/users/createUser";

const UserEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  if (!id) {
    navigate("/users");
    return null;
  }

  // 사용자 정보 조회
  const {
    data: user,
    isLoading: isLoadingUser,
    error: userError,
  } = useQueryGetUser(id);

  // 수정 API 훅
  const { mutate: updateStudent, isPending: isUpdating } = useUpdateStudent(id);
  const [serverError, setServerError] = useState<string | undefined>();

  // 폼 제출 핸들러
  const handleSubmit = (formData: ICreateStudentRequest) => {
    if (!user) return;

    // 업데이트 데이터 준비
    const updateData: IUpdateStudentRequest = {
      email: formData.email,
      name: formData.name,
      grade: formData.grade,
      is_graduated: formData.is_graduated,
      school: formData.school,
      phone: formData.phone,
      parent_phone: formData.parent_phone,
      memo: formData.memo,
      group_id: formData.group_id,
    };

    // 비밀번호가 있는 경우에만 포함
    if (formData.password) {
      updateData.password = formData.password;
    }

    // API 호출
    updateStudent(updateData, {
      onSuccess: () => {
        // 성공 시 상세 페이지로 이동
        navigate(`/users/${id}`);
      },
      onError: (error: any) => {
        // 서버 오류 처리
        setServerError(error?.message || "Failed to update student");
      },
    });
  };

  // 상세 페이지로 돌아가기
  const handleCancel = () => {
    navigate(`/users/${id}`);
  };

  // 로딩 중
  if (isLoadingUser) {
    return (
      <PageContainer>
        <div className="p-8 text-center">
          <Text>Loading student information...</Text>
        </div>
      </PageContainer>
    );
  }

  // 오류 발생
  if (userError || !user) {
    return (
      <PageContainer>
        <div className="p-8 text-center text-red-600">
          <Text>Failed to load student information. Please try again.</Text>
          <Button onClick={() => navigate("/users")} className="mt-4">
            Back to List
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button plain onClick={handleCancel}>
            <ArrowLeftIcon className="h-5 w-5" />
            Back
          </Button>

          <div>
            <Heading>Edit Student: {user.name}</Heading>
            <Text className="text-zinc-500">
              Update the information below to modify this student's account.
            </Text>
          </div>
        </div>

        {/* 학생 수정 폼 */}
        <div className="p-8 border border-gray-200 rounded-md bg-white">
          <StudentForm
            initialData={{
              ...user,
              group_id: user.group_id || "",
            }}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isLoading={isUpdating}
            serverError={serverError}
            isEditMode={true}
          />
        </div>
      </div>
    </PageContainer>
  );
};

export default UserEditPage;
